import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Sparkles, Loader2, Trash2, Download } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, errMsg, dt } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, ProjectSelect, Badge2, Empty } from "@/components/common";
import ReportRenderer from "@/components/ReportRenderer";
import EntityPage from "@/components/EntityPage";
import ShareButton from "@/components/ShareDialog";
import { ResearchPanel } from "@/components/AiTools";
import ObjectiveAdvisor from "@/components/am/ObjectiveAdvisor";

export const REPORT_CFG = {
  project_intelligence: { label: "Plan", title: "Project Intelligence", sub: "Location, demand, competition & pricing analysis with an AI success score." },
  buyer_intelligence: { label: "Plan", title: "Buyer Intelligence", sub: "Buyer personas, income groups, industry segments, community & NRI analysis." },
  community_intelligence: { label: "Plan", title: "Community Intelligence", sub: "Demand scores for Marathi, Gujarati, Jain, Punjabi, South Indian, Muslim, NRI & other communities." },
  corporate_intelligence: { label: "Plan", title: "Corporate Intelligence", sub: "Discover nearby companies, employee counts, distance and opportunity scores.", entity: "corporate_accounts" },
  competitor_intelligence: { label: "Plan", title: "Competitor Intelligence", sub: "Competitor pricing, inventory, offers and positioning.", entity: "competitors" },
  brand_positioning: { label: "Market", title: "Brand Positioning Engine", sub: "USPs, messaging pillars, taglines and positioning recommendations." },
  brand_ambassador: { label: "Market", title: "Brand Ambassador Engine", sub: "Celebrities, regional stars, influencers, sports personalities & AI ambassadors with cost, reach and ROI.",
    inputs: [["budget_inr", "Ambassador budget (INR)"], ["region", "Target region"]] },
  marketing_plan: { label: "Market", title: "Marketing Planner", sub: "Complete digital + offline media plan with budget allocation, timeline and ROI forecast.",
    inputs: [["budget_inr", "Total budget (INR)"], ["duration_days", "Duration (days)"], ["target_units", "Units to sell"], ["goal", "Primary goal"]] },
  launch_orchestration: { label: "Launch", title: "Launch Orchestration Engine", sub: "Best launch date & time, festival alignment, multi-channel launch plan and readiness score.",
    inputs: [["preferred_window", "Preferred launch window"], ["launch_budget", "Launch budget (INR)"]] },
  daily_advisor: { label: "Command", title: "AI Daily Advisor", sub: "Daily recommendations, risks and opportunities across your portfolio.", noProject: true },
};

async function downloadPdf(report) {
  try {
    const r = await api.get(`/ai/reports/${report.id}/pdf`, { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = `LaunchX360-${report.type}-${report.created_at.slice(0, 10)}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  } catch (e) { toast.error("Could not generate PDF"); }
}

export default function AIReports({ type }) {
  const cfg = REPORT_CFG[type];
  const { can, reload, me } = useAuth();
  const [params] = useSearchParams();
  const [projectId, setProjectId] = useState(params.get("project") || "");
  const [inputs, setInputs] = useState({});
  const [reports, setReports] = useState([]);
  const [active, setActive] = useState(null);
  const [busy, setBusy] = useState(false);
  const [entKey, setEntKey] = useState(0);
  const locked = me?.features && !me.features.includes(type);

  const load = useCallback(async () => {
    const r = await api.get("/ai/reports", { params: { type, ...(cfg.noProject ? {} : { project_id: projectId || undefined }) } });
    setReports(r.data);
    setActive((a) => r.data.find((x) => x.id === a?.id) || r.data[0] || null);
    return r.data;
  }, [type, projectId, cfg.noProject]);

  useEffect(() => { setActive(null); if (cfg.noProject || projectId) load(); }, [load]); // eslint-disable-line
  useEffect(() => {
    if (!reports.some((r) => r.status === "processing")) return;
    const t = setInterval(async () => {
      const rs = await load();
      if (!rs.some((r) => r.status === "processing")) { reload(); setEntKey((k) => k + 1); }
    }, 3500);
    return () => clearInterval(t);
  }, [reports, load, reload]);

  const generate = async () => {
    setBusy(true);
    try {
      const r = await api.post("/ai/reports", { type, project_id: cfg.noProject ? null : projectId, inputs });
      toast.success(`Generating ${cfg.title}. This takes 20-60 seconds.`);
      setActive(r.data);
      await load();
      reload();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const remove = async (id) => { await api.delete(`/ai/reports/${id}`); load(); };

  return (
    <div data-testid={`ai-report-page-${type}`}>
      <PageHeader label={cfg.label} title={cfg.title} subtitle={cfg.sub}>
        <span className="lx-ai-badge"><Sparkles className="h-3 w-3" />AI-powered · OpenAI</span>
      </PageHeader>
      {(type === "project_intelligence" || type === "competitor_intelligence") && <ResearchPanel context={type === "competitor_intelligence" ? "competitor" : "project"} />}
      {type === "marketing_plan" && <div className="mb-6"><ObjectiveAdvisor projectId={projectId} /></div>}
      <div className="lx-card mb-6 flex flex-col lg:flex-row gap-4 lg:items-end">
        {!cfg.noProject && <div className="space-y-1.5"><Label className="text-xs text-slate-400">Project</Label><ProjectSelect value={projectId} onChange={setProjectId} className="w-64" /></div>}
        {(cfg.inputs || []).map(([k, l]) => (
          <div key={k} className="space-y-1.5 flex-1 min-w-[140px]"><Label className="text-xs text-slate-400">{l}</Label><Input value={inputs[k] || ""} onChange={(e) => setInputs({ ...inputs, [k]: e.target.value })} data-testid={`report-input-${k}`} /></div>
        ))}
        <Button onClick={generate} disabled={busy || locked || !can("intelligence", "write") || (!cfg.noProject && !projectId)} data-testid="report-generate-button" className="lg:ml-auto">
          {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}{locked ? "Upgrade plan to unlock" : "Generate with AI"}
        </Button>
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-[260px_1fr] gap-6">
        <div className="space-y-2" data-testid="report-history">
          <div className="text-[11px] font-mono uppercase tracking-widest text-slate-500 mb-2">Report history</div>
          {!reports.length && <div className="text-sm text-slate-500">No reports yet.</div>}
          {reports.map((r) => (
            <div key={r.id} onClick={() => setActive(r)} role="button" data-testid={`report-history-item-${r.id}`}
              className={`rounded-lg border p-3 cursor-pointer transition-colors ${active?.id === r.id ? "border-emerald-500/50 bg-emerald-500/5" : "border-slate-800 hover:border-slate-700"}`}>
              <div className="flex justify-between items-center"><Badge2>{r.status}</Badge2>{can("intelligence", "write") && <Trash2 className="h-3.5 w-3.5 text-slate-500 hover:text-rose-400" onClick={(e) => { e.stopPropagation(); remove(r.id); }} />}</div>
              <div className="text-xs text-slate-400 mt-2 font-mono">{dt(r.created_at)} · {r.credits} cr</div>
            </div>
          ))}
        </div>
        <div>
          {!active && <Empty title="No report selected">Choose a project and click Generate with AI.</Empty>}
          {active?.status === "processing" && (
            <div className="lx-card py-16 text-center" data-testid="report-processing"><Loader2 className="h-8 w-8 animate-spin text-cyan-400 mx-auto" /><div className="mt-4 font-semibold">AI is analysing...</div><div className="text-sm text-slate-500 mt-1">Pulling market signals, project data and competition.</div></div>
          )}
          {active?.status === "failed" && <div className="lx-card border-rose-500/30 text-rose-300 text-sm" data-testid="report-failed">Generation failed: {active.error}. Credits were refunded.</div>}
          {active?.status === "completed" && (
            <>
              <div className="flex justify-end gap-2 mb-3"><ShareButton kind="ai_report" refId={active.id} /><Button variant="secondary" onClick={() => downloadPdf(active)} data-testid="report-download-pdf-button"><Download className="h-4 w-4 mr-2" />Download branded PDF</Button></div>
              <ReportRenderer data={active.data} />
            </>
          )}
        </div>
      </div>
      {cfg.entity && (
        <div className="mt-10">
          <div className="text-lg font-bold mb-4">Tracked {cfg.entity === "competitors" ? "Competitors" : "Corporate Accounts"}</div>
          <EntityPage entity={cfg.entity} reloadKey={entKey} />
        </div>
      )}
    </div>
  );
}
